import React, { FC, useEffect, useRef, forwardRef, type ComponentType } from "react";

import ReactDOM from "react-dom";
import { twMerge } from "tailwind-merge";

import { withErrorHandler } from "Cobalt/ErrorHandler";
import type { ErrorHandlerProps } from "Cobalt/ErrorHandler";
import { TitleSmall } from "Cobalt/Header";
import Icon from "Common/Icon";

export interface ModalHeaderProps {
  closeButton?: boolean;
  closeLabel?: string;
  /** @deprecated ModalHeader no longer requires onHide for close buttons to function */
  onHide?: () => void;
  className?: string;
  children?: React.ReactNode;
}

export const ModalHeader: FC<ModalHeaderProps> = ({ closeButton, closeLabel = "Close", className, children, ...props }) => (
  <header
    className={twMerge("si-modal-header border-solid border-0 border-b border-neutral-200 p-4 flex flex-row gap-2 justify-between", className)}
    {...props}
  >
    {children}
    {closeButton && (
      <button
        type="button"
        data-dismiss
        className="si-btn si-btn-circle si-btn-ghost si-btn-neutral si-btn-sm h-6 w-6 text-base leading-none"
        aria-label={closeLabel}
        onClick={props.onHide}
      >
        <Icon icon="times" />
      </button>
    )}
  </header>
);

export interface ModalTitleProps {
  className?: string;
  children?: React.ReactNode;
}

export const ModalTitle: FC<ModalTitleProps> = ({ className, children, ...props }) => (
  <TitleSmall m={0} {...props}>
    {children}
  </TitleSmall>
);

export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children?: React.ReactNode;
}

export const ModalBody: FC<ModalBodyProps> = ({ className, children, ...props }) => (
  <div className={twMerge("si-modal-body", "p-4", className)} {...props}>
    {children}
  </div>
);

export interface ModalFooterProps {
  className?: string;
  children?: React.ReactNode;
}

export const ModalFooter: FC<ModalFooterProps> = ({ className, children, ...props }) => (
  <div className={twMerge("si-modal-footer si-modal-action border-solid border-0 border-t border-neutral-200 p-4 m-0", className)} {...props}>
    {children}
  </div>
);

export interface ModalDialogProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  size?: "lg" | "large" | "sm" | "small";
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export const ModalDialog: FC<ModalDialogProps> = ({ className, size, style, children, ...props }) => (
  <div role="document" className={twMerge("si-modal-box rounded fixed sm:top-nav p-0", className)} style={style} {...props}>
    {children}
  </div>
);

export interface ModalProps {
  show: boolean;
  onHide: () => void;
  /** @deprecated Cobalt Modals utilize HTML dialog elements instead of backdrop overlay elements */
  backdrop?: boolean | "static";
  /** @deprecated Cobalt Modals utilize HTML dialog elements instead of backdrop overlay elements */
  backdropClassName?: string;
  keyboard?: boolean;
  /** @deprecated Not supported, please provide your widths with classNames e.g. "w-screen-xl" */
  size?: "lg" | "large" | "sm" | "small";
  name?: string;
  className?: string;
  dialogClassName?: string;
  /** @deprecated Never used */
  dialogComponentClass?: React.ElementType;
  children?: React.ReactNode | JSX.Element;
}

const ModalComponent: FC<ModalProps> = ({
  name = "modal",
  show,
  onHide,
  backdrop: _backdrop = true,
  keyboard = true,
  size,
  className,
  dialogClassName,
  dialogComponentClass: DialogComponent = ModalDialog,
  children,
  ...props
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogElement = dialogRef.current;

    if (dialogElement) {
      if (show && !dialogElement.open) {
        dialogElement.showModal();
      } else if (!show && dialogElement.open) {
        dialogElement.close();
      }
    }

    return () => {
      if (dialogElement && dialogElement.open) {
        dialogElement.close();
      }
    };
  }, [show]);

  const handleClose = () => {
    if (onHide) {
      onHide();
    }

    if (dialogRef.current?.open) {
      dialogRef.current.close();
    }
  };

  const handleDialogClick = (e: React.MouseEvent<HTMLDialogElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest("[data-dismiss]")) {
      handleClose();
    }
  };

  const handleCancel = (e: React.SyntheticEvent<HTMLDialogElement>) => {
    e.preventDefault();
    if (keyboard) {
      handleClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>) => {
    if (e.key === "Escape" && keyboard) {
      handleClose();
    }
  };

  const dialogModalClasses = twMerge("si-modal border-none si-modal-top sm:si-modal-middle", className);

  if (!show) {
    return null;
  }

  // Render at the document root (standard practice)
  return ReactDOM.createPortal(
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */
    <dialog
      {...props}
      ref={dialogRef}
      className={dialogModalClasses}
      onClickCapture={handleDialogClick}
      onCancel={handleCancel}
      aria-modal="true"
      aria-labelledby={name}
      onKeyDown={handleKeyDown}
    >
      <DialogComponent className={dialogClassName} size={size} onClick={(e) => e.stopPropagation()}>
        {children}
      </DialogComponent>
    </dialog>,
    document.body,
  );
};

// Wrap with error handling while preserving the public Modal props API.
export const Modal: FC<ModalProps> = withErrorHandler(
  forwardRef<HTMLDialogElement, ModalProps>((props, _ref) => <ModalComponent {...props} />) as unknown as ComponentType<ModalProps & ErrorHandlerProps>,
  { hideOnError: true },
) as FC<ModalProps>;
